/* eslint-disable react/require-default-props */
import { StandardTagger, ListboxOption } from '@brandfolder/react';
import {  t } from '@lingui/macro';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { getIdentities, IdentityResponse } from '@api/v4/private/people_tagging/faces';
interface PeopleFilterProps {
  filters: {
    people?: string[];
  };
  updateFilters: (id: string, name: string) => void;
}

const PeopleFilterWrapper = styled.div`
  padding: 20px 25px;
  border-bottom: 1px solid rgb(230, 230, 230);

  textarea.bf-combobox__combobox {
    width: calc(100% - 10px);
  }

  .bf-textfield__body{
    .bf-textfield__addons{
      .bf-textarea__fake{
        min-height: fit-content;
        height: 48px;
        padding: 0;
        .bf-textfield__wrapper{
          height: 100%;
        }
      }
    }
  }
`;

const FaceImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
`;

const PeopleFilter: React.FunctionComponent<PeopleFilterProps> = ({
  filters,
  updateFilters,
}) => {
  const [availableIdentities, setAvailableIdentities] = useState<IdentityResponse[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [clearTags, setClearTags] = useState(false);
  const selectedPeopleIds = filters.people || [];

  // Clear tags when filters.people becomes empty (e.g., Clear Filters button)
  useEffect(() => {
    if (selectedPeopleIds.length === 0) {
      setClearTags(true);
    }
  }, [selectedPeopleIds]);

  // Reset clearTags after it's been applied
  useEffect(() => {
    if (clearTags) {
      setClearTags(false);
    }
  }, [clearTags]);

  useEffect(() => {
    const fetchPeopleData = async (): Promise<void> => {
      try {
        const brandfolderKey = BFG.brandfolder_key;
        if (!brandfolderKey) {
          return;
        }

        const response = await getIdentities({ brandfolderKey });

        const identities: IdentityResponse[] = response
          .filter((identity) => identity.name && identity.name.trim() !== '');

        setAvailableIdentities(identities);

      } catch (error) {
        console.error('Error fetching people data:', error);
      }
    };

    fetchPeopleData();
  }, []);

  const handleTaggedChange = (tags: ListboxOption[]): void => {
    try {
      const newIds = tags
        .filter((tag) => tag && tag.value != null)
        .map((tag) => typeof tag.value === 'string' ? tag.value : String(tag.value));

      // Find the difference to determine what was added or removed
      const addedId = newIds.find((id) => !selectedPeopleIds.includes(id));
      const removedId = selectedPeopleIds.find((id) => !newIds.includes(id));

      const changedId = addedId || removedId;
      if (changedId) {
        const identity = availableIdentities.find((i) => i.id === changedId);
        if (identity) {
          updateFilters(changedId, identity.name);
        }
      }
    } catch (error) {
      console.error('Error in handleTaggedChange:', error);
    }
  };

  const taggerOptions: ListboxOption[] = useMemo(() => {
    return availableIdentities.map((identity) => ({
      key: identity.id,
      label: identity.name,
      value: identity.id,
      children: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaceImage alt={identity.name}  src={identity.identity_thumbnail_url} />
          <span>{identity.name}</span>
        </div>
      ),
    }));
  }, [availableIdentities]);

  const filteredOptions: ListboxOption[] = useMemo(() => {
    if (!searchValue.trim()) {
      return taggerOptions;
    }
    const query = searchValue.toLowerCase();
    return taggerOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [taggerOptions, searchValue]);

  const initialTags: ListboxOption[] = useMemo(() => {
    return selectedPeopleIds
      .map((id) => {
        const identity = availableIdentities.find((i) => i.id === id);
        if (identity) {
          return {
            key: identity.id,
            label: identity.name,
            value: identity.id,
            children: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaceImage alt={identity.name}  src={identity.identity_thumbnail_url} />
                <span>{identity.name}</span>
              </div>
            ),
          };
        }
        return null;
      })
      .filter(Boolean) as ListboxOption[];
  }, [selectedPeopleIds, availableIdentities]);

  if (availableIdentities.length === 0) {
    return null;
  }

  return (
    <PeopleFilterWrapper>
      <StandardTagger
        allowCustomValue={false}
        allowDuplicates={false}
        clearTags={clearTags}
        comboboxProps={{
          comboboxTextareaProps: {
            onChange: (e) => {
              setSearchValue(e.target.value);
            },
            placeholder: t`Search people...`,
          }
        }}
        id="people-filter-tagger"
        initialTags={initialTags}
        labels={{
          label: t`People`,
          emptyLabel: t`No people found`,
          listboxLabel: t`List of people`,
          removeTagLabel: t`Remove person`,
          iconButtonLabel: t`Toggle list of people`,
        }}
        onTagged={(tags) => handleTaggedChange(tags)}
        options={filteredOptions}
        search={false}
        showLabel={true}
      />
    </PeopleFilterWrapper>
  );
};

PeopleFilter.propTypes = {
  filters: PropTypes.shape({
    people: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  updateFilters: PropTypes.func.isRequired,
};

export default PeopleFilter;
